import React, { useMemo } from "react";

function QrImage({ url, size = 120 }) {
  const src = useMemo(() => {
    if (!url) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}`;
  }, [url, size]);

  if (!src) return null;
  return (
    <div className="share-cert-qr">
      <img src={src} alt="Certificate verification QR code" width={size} height={size} />
    </div>
  );
}

/**
 * Premium share certificate layout — used by verify page and preview modal.
 */
export default function ShareCertificateDocument({ data, printId = "share-certificate-print" }) {
  const valid = Boolean(data?.valid);
  const company = data?.company_name || "Need Healthcare";
  const initials = company
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <article className="share-cert-card" id={printId}>
      <div className="share-cert-watermark" aria-hidden>
        {initials}
      </div>
      <div className="share-cert-inner">
        <header className="share-cert-header">
          <div className="share-cert-brand">
            <div className="share-cert-logo" aria-hidden>
              {initials}
            </div>
            <span className="share-cert-company">{company}</span>
          </div>
          <div className="share-cert-meta">
            <span>Certificate No.</span>
            <strong>{data?.certificate_number || "—"}</strong>
            <span style={{ display: "block", marginTop: "0.35rem" }}>Issued {data?.issue_date || "—"}</span>
          </div>
        </header>

        <div className="share-cert-title-block">
          <h1 className="share-cert-title">Share Certificate</h1>
          <p className="share-cert-subtitle">Official project-wise share ownership confirmation</p>
        </div>

        {!valid ? (
          <p className="msg msg-error" style={{ textAlign: "center" }}>
            {data?.detail || "This certificate is not currently valid."}
          </p>
        ) : null}

        {valid ? (
          <>
            <section className="share-cert-section" aria-labelledby="cert-investor">
              <h2 id="cert-investor" className="share-cert-section-title">
                Investor &amp; project
              </h2>
              <dl className="share-cert-grid">
                <div className="share-cert-field">
                  <dt>Investor</dt>
                  <dd>
                    {data.investor?.name}
                    <span className="share-cert-field-sub">ID {data.investor?.id}</span>
                  </dd>
                </div>
                <div className="share-cert-field">
                  <dt>Project</dt>
                  <dd>
                    {data.project?.number}
                    {data.project?.name ? ` — ${data.project.name}` : ""}
                  </dd>
                </div>
                <div className="share-cert-field">
                  <dt>Ownership</dt>
                  <dd>{data.ownership_pct}</dd>
                </div>
                <div className="share-cert-field">
                  <dt>Status</dt>
                  <dd>{data.share_status}</dd>
                </div>
              </dl>
            </section>

            <section className="share-cert-section" aria-labelledby="cert-shares">
              <h2 id="cert-shares" className="share-cert-section-title">
                Shareholding
              </h2>
              <div className="share-cert-highlights">
                <div className="share-cert-highlight">
                  <span>Shares held</span>
                  <strong>{data.shares?.total_held}</strong>
                </div>
                <div className="share-cert-highlight">
                  <span>Total invested</span>
                  <strong>{data.shares?.total_investment_bdt} BDT</strong>
                </div>
                <div className="share-cert-highlight share-cert-highlight--value">
                  <span>Current value</span>
                  <strong>{data.shares?.current_valuation_bdt} BDT</strong>
                </div>
                <div className="share-cert-highlight">
                  <span>First purchase</span>
                  <strong>{data.shares?.purchase_date}</strong>
                </div>
              </div>
            </section>
          </>
        ) : null}

        <section className="share-cert-section" aria-labelledby="cert-verify">
          <h2 id="cert-verify" className="share-cert-section-title">
            Verification &amp; security
          </h2>
          <div className="share-cert-verify">
            {valid && data.verify_url ? <QrImage url={data.verify_url} /> : null}
            <div>
              <span className={`share-cert-badge${valid ? "" : " share-cert-badge--invalid"}`}>
                {valid ? "Verified · Live holding" : "Not verified"}
              </span>
              <p>
                {valid
                  ? "Scan the QR code to confirm this certificate against live platform records."
                  : "This certificate could not be verified against current holdings."}
              </p>
            </div>
          </div>
        </section>

        <footer className="share-cert-footer">
          <span>Digitally issued · Institutional record</span>
        </footer>
      </div>
    </article>
  );
}
